import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentLocationName, selectPoint, selectSelectedLayers, setLocationName } from "../features/map/mapSlice";
import { getUserLocation } from "../Map/userLocation";
import { describeSpot, latLonText, lookUpNearPlace } from "../Map/nearPlace";
import { notePlaceSearched } from "../Map/placeSearched";
import { fetchPlaces, goToPlace, matchesSearch, parseSearch, searchTuning, toPlace } from "./Places";
import { fromLonLat } from "../Map/mercator";
import { track } from "../track";
import "./PlaceSearch.css";

// The place search in the top bar. On desktop it is a wide field whose results drop
// down under it; on phones the field in the header opens a full-screen search with
// the keyboard up. Both use the same place lookup as the Place dropdown (/place2,
// Open Names places and NaPTAN stations) and move the map the same way when a place is picked.
//
// Results come grouped: the best match first, then postcodes, areas, towns, stations,
// buildings, and streets and other places. An empty box offers the current location
// and the last few places picked. Arrow keys move, Enter picks, and Enter straight
// after typing takes the top match once it has arrived. "/" anywhere jumps here.
//
// Once a location is chosen its name stays in the field: the place picked, or for a
// spot chosen on the map (long-press, current location) the nearest
// street or postcode from /nearplace. Clicking the field empties it so a new one can be
// typed; there is no button to clear the location itself.
//
// The landing page uses it too (landing/Landing.js), with its own placeholder, without
// the chosen location's name, and with onChoose to open the map once a place is picked.
// With detached (the listing page's Find the address map, account/AddressMap.js) the map's
// location in the store is left alone: onChoose alone gets the place ({x, y} in EPSG:3857),
// the current location included.

// The server needs at least this many characters of name
const MIN_CHARS = 3;
// How many rows each group shows
const GROUP_SIZE = 5;
const RECENTS_KEY = 'recentPlaces';
const MAX_RECENTS = 5;
// Wait this long after a keypress before asking the server
const DEBOUNCE_MS = 120;

// Anything can ask the search to take focus (e.g. the Where line in the menu)
export const FOCUS_SEARCH_EVENT = 'tms:focus-place-search';
export const focusPlaceSearch = () => window.dispatchEvent(new Event(FOCUS_SEARCH_EVENT));

const norm = (s) => (s || '').toLowerCase().replace(/\s+/g, '');

// A whole postcode, as the names of postcode rows are written
const FULL_POSTCODE = /^[A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2}$/;
// A postcode typed without its space, split into outward and inward parts
const UNSPACED_POSTCODE = /^([A-Z]{1,2}\d[A-Z\d]?)(\d[A-Z]{0,2})$/;

// "sw1v1ju" -> "SW1V 1JU", "b185" -> "B18 5". Postcode names in ukmap.places have the
// space, and the server matches on the start of the name, so a postcode typed without
// it would find nothing. Three characters ("e14", "n11") are left alone: they are more
// likely a whole district than a district plus sector.
export const postcodeWithSpace = (text) => {
    const t = (text || '').trim();
    if (/\s/.test(t) || t.length<4) return null;
    const m = t.toUpperCase().match(UNSPACED_POSTCODE);
    return m ? m[1]+' '+m[2] : null;
};

const looksLikePostcode = (text) => /^[a-z]{1,2}\d/i.test((text || '').trim());

const GROUPS = [
    {key:'postcode', title:'Postcodes'},
    {key:'area', title:'Areas'},
    {key:'town', title:'Towns'},
    {key:'station', title:'Stations'},
    {key:'building', title:'Buildings'},
    {key:'other', title:'Streets and other places'},
];

// Which group a place goes under, from the category PlaceData2 gives it
// (java/src/net/propstats/data/opennames_priority.csv). Postcodes have no category.
export const groupOf = (place) => {
    const cat = place.category;
    if (!cat) return FULL_POSTCODE.test(place.name) ? 'postcode' : 'other';
    if (cat==='City' || cat==='Town' || cat==='Village' || cat==='Hamlet') return 'town';
    if (cat==='Area' || cat==='Place') return 'area';
    if (cat==='Station') return 'station';
    if (cat==='Building') return 'building';
    return 'other';
};

const typeLabel = (place) => (groupOf(place)==='postcode' ? 'Postcode' : (place.category || 'Place'));

// Which kind of place wins the top match when names match equally well. A query that
// starts like a postcode puts postcodes first.
const kindRank = (place, postcodeQuery) => {
    const group = groupOf(place);
    if (group==='postcode') return postcodeQuery ? -1 : 4;
    return {City:0, Town:1, Area:2, Village:3, Place:3, Hamlet:5, Building:6, Road:6.5, Station:6.5}[place.category] ?? 7;
};

const compareMatches = (nq, postcodeQuery) => (a, b) => {
    const score = (p) => {
        const nn = norm(p.name);
        return nn===nq ? 0 : (nn.startsWith(nq) ? 1 : 2);
    };
    return score(a)-score(b) || kindRank(a, postcodeQuery)-kindRank(b, postcodeQuery)
        || a.name.length-b.name.length || a.name.localeCompare(b.name);
};

// The second line under a place: the area or town it sits in, e.g. "Birmingham" or
// "B18, Birmingham" for a building, station or street
export const subtitle = (place) => {
    const parts = [];
    const add = (val) => {
        if (!val) return;
        const v = String(val);
        if (v.toLowerCase()===place.name.toLowerCase()) return;
        if (parts.some((p) => p.toLowerCase()===v.toLowerCase())) return;
        parts.push(v);
    };
    const group = groupOf(place);
    if (group==='building' || group==='station' || group==='other') add(place.postcode_district);
    add(place.populated_place);
    add(place.county);
    if (parts.length===0) add(place.region);
    if (parts.length===0) add(place.country);
    return parts.slice(0, 2).join(', ');
};

// The name with the typed text in bold, matched ignoring spaces so "b185" marks "B18 5"
const highlight = (name, query) => {
    const nq = norm(query);
    if (!nq) return name;
    const idx = [];
    for (let i = 0; i<name.length; i++) if (!/\s/.test(name[i])) idx.push(i);
    const pos = norm(name).indexOf(nq);
    if (pos<0) return name;
    const a = idx[pos];
    const b = idx[pos+nq.length-1]+1;
    return <>{name.slice(0, a)}<mark>{name.slice(a, b)}</mark>{name.slice(b)}</>;
};

const samePlace = (a, b) => a.name===b.name && a.x===b.x && a.y===b.y;

// Recent places live in localStorage. The first time, the Place dropdown's history is used.
const readRecents = () => {
    try {
        const stored = localStorage.getItem(RECENTS_KEY);
        if (stored) return JSON.parse(stored).map(toPlace).filter((p) => p.name).slice(0, MAX_RECENTS);
        const history = JSON.parse(localStorage.getItem('placeHistory') || 'null');
        if (history && history.places) return history.places.map(toPlace).filter((p) => p.name).slice(0, MAX_RECENTS);
    }
    catch (e) {
        console.log(e);
    }
    return [];
};

const writeRecents = (places) => {
    try {
        localStorage.setItem(RECENTS_KEY, JSON.stringify(places));
    }
    catch (e) {
        console.log(e);
    }
};

const RECENTS_EVENT = 'tms:recent-places-changed';

// Adds a place to the front of the recent places, from here or elsewhere in the app
// (a dropped pin), and tells the search to show the new list
export const addRecentPlace = (place) => {
    const next = [place, ...readRecents().filter((p) => !samePlace(p, place))].slice(0, MAX_RECENTS);
    writeRecents(next);
    window.dispatchEvent(new Event(RECENTS_EVENT));
};

/*
 * Turns the matches for a query into the sections the list draws: the top match on
 * its own, then up to GROUP_SIZE of each group. Every selectable row gets an index,
 * which is what the arrow keys move through.
 */
export const buildSections = (places, query) => {
    const {namePart} = parseSearch(query);
    const nq = norm(namePart);
    const postcodeQuery = looksLikePostcode(namePart);
    const sorted = places.slice().sort(compareMatches(nq, postcodeQuery));
    const sections = [];
    if (sorted.length===0) return sections;
    const top = sorted[0];
    sections.push({title:'Top match', items:[{kind:'place', place:top}]});
    GROUPS.forEach((group) => {
        let hits = sorted.filter((p) => p!==top && groupOf(p)===group.key);
        if (group.key==='postcode') hits = hits.sort((a, b) => a.name.localeCompare(b.name));
        hits = hits.slice(0, GROUP_SIZE);
        if (hits.length>0) sections.push({title:group.title, items:hits.map((place) => ({kind:'place', place}))});
    });
    return sections;
};

const ICONS = {
    postcode: <path d="M8 14.5S3 10 3 6.5a5 5 0 0110 0C13 10 8 14.5 8 14.5z M8 4.8a1.7 1.7 0 110 3.4 1.7 1.7 0 010-3.4z"/>,
    area: <path d="M2 4l4-1.5 4 1.5 4-1.5v9.5l-4 1.5-4-1.5-4 1.5z M6 2.5v9.5M10 4v9.5"/>,
    town: <path d="M2 14V7l3-2 3 2v7M8 14V4l3-2 3 2v10M1 14h14"/>,
    station: <><path d="M4 12V5c0-2 1.6-3 4-3s4 1 4 3v7M3 12h10M5 12l-2 4M11 12l2 4M4 7h8"/><path d="M6 10h.01M10 10h.01"/></>,
    building: <path d="M3 14V3h7v11M10 7h3v7M1 14h14M5.5 6h2M5.5 9h2"/>,
    other: <path d="M5 14L6.8 2M11 14L9.2 2M8 3.5v1.6M8 7.2v1.6M8 11v1.6"/>,
    recent: <><circle cx="8" cy="8" r="6"/><path d="M8 4.5V8l2.5 1.5"/></>,
    location: <><circle cx="8" cy="8" r="4.5"/><circle cx="8" cy="8" r="1.4" fill="currentColor"/><path d="M8 1v2.5M8 12.5V15M1 8h2.5M12.5 8H15"/></>,
};

const Icon = ({name, size=16}) => (
    <svg viewBox="0 0 16 16" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{ICONS[name]}</svg>
);

const SearchIcon = () => (
    <svg className="mag" viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" aria-hidden="true"><circle cx="7" cy="7" r="4.6"/><path d="M10.4 10.4L14 14"/></svg>
);

const LockIcon = () => (
    <svg className="lock" width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor" d="M17 9h-1V7a4 4 0 0 0-8 0v2H7a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2Zm-7-2a2 2 0 0 1 4 0v2h-4V7Z"/>
    </svg>
);

// Kept at module scope so the list is not remounted on every keystroke
const ResultsList = ({listId, view, sel, setSel, choose, query}) => {
    useEffect(() => {
        const el = document.getElementById(listId+'-opt-'+sel);
        if (el && el.scrollIntoView) el.scrollIntoView({block:'nearest'});
    }, [listId, sel, view]);

    return (
        <div className="tms-search-results" id={listId} role="listbox" aria-label="Places">
            {view.sections.map((section, s) => (
                <div role="group" key={section.title || s} aria-label={section.title || undefined}>
                    {section.title && <div className="g" aria-hidden="true">{section.title}</div>}
                    {section.items.map((item) => {
                        const on = item.index===sel;
                        let icon, name, sub, label;
                        if (item.kind==='location') {
                            icon = 'location';
                            name = 'Current location';
                            sub = 'Uses your device location';
                        }
                        else {
                            icon = item.kind==='recent' ? 'recent' : groupOf(item.place);
                            name = item.kind==='recent' ? item.place.name : highlight(item.place.name, parseSearch(query).namePart);
                            sub = subtitle(item.place);
                            label = typeLabel(item.place);
                        }
                        return (
                            <div key={item.index} id={listId+'-opt-'+item.index} role="option" aria-selected={on}
                                className={'r'+(on ? ' on' : '')}
                                onMouseDown={(e) => e.preventDefault()}
                                onMouseMove={() => { if (!on) setSel(item.index); }}
                                onClick={() => choose(item)}>
                                <span className={'ico '+icon}><Icon name={icon}/></span>
                                <span className="txt">
                                    <span className="name">{name}</span>
                                    {sub && <em>{sub}</em>}
                                </span>
                                {label && <span className="t">{label}</span>}
                            </div>
                        );
                    })}
                </div>
            ))}
            {view.message && <div className="msg">{view.message}</div>}
        </div>
    );
};

// The name of the chosen location, looked up from /nearplace when it was chosen on the
// map rather than picked by name. '' while there is no location or it is being named.
const useLocationName = () => {
    const dispatch = useDispatch();
    const point = useSelector(selectPoint);
    const name = useSelector(selectCurrentLocationName);
    const hasPoint = !!(point && point.x && point.y);

    useEffect(() => {
        if (!hasPoint || name) return;
        let live = true;
        lookUpNearPlace(point).then((near) => {
            if (!live) return;
            const spot = describeSpot(near);
            dispatch(setLocationName({name:spot ? spot.name : latLonText(point), x:point.x, y:point.y}));
        });
        return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [point, name]);

    return name;
};

const ClearIcon = () => (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8"
        strokeLinecap="round" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8"/></svg>
);

// onEnterText, when given, sees the typed text first when Enter is pressed: returning true
// means it dealt with it (the landing page's search takes listing links and postcodes itself)
// textAction, when given, can claim typed text that isn't a place (the landing page's listing
// links): it returns the rows shown instead of place matches, [{label, sub, run}] (run when the
// row is chosen; a row without run hands the text to onEnterText), or null to search for places
// as usual. Enter hands the text to onEnterText, which takes the first row's choice.
// onLocked, when given, means searching is for subscribers (a Big Picture map opened by someone
// without one, account/MapGate.js): the box is drawn but searches nothing, and clicking it, or
// "/", calls onLocked, which shows the map's paywall.
export default function PlaceSearch({isMobile, placeholder, showLocation=true, onChoose, detached=false, onEnterText=null, textAction=null, onLocked=null}) {
    const dispatch = useDispatch();
    const selectedLayers = useSelector(selectSelectedLayers);
    const storeLocationName = useLocationName();
    const locationName = showLocation ? storeLocationName : '';

    const [query, setQuery] = useState('');
    // Matches for the query they were fetched for - may lag behind what is typed
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [sel, setSel] = useState(0);
    const [recents, setRecents] = useState(readRecents);

    const rootRef = useRef();
    const inputRef = useRef();
    const requestId = useRef(0);
    // Replies already in hand, keyed on name|area, so typing on narrows without a fetch
    const cache = useRef(new Map());
    // Enter was pressed before the matches for the typed text arrived
    const pendingEnter = useRef(false);

    const trimmed = query.trim();

    // A reply that holds every match for a shorter name prefix answers a longer one too
    const lookup = async (name, area) => {
        const n = name.toLowerCase();
        const a = area.toLowerCase();
        for (const [key, val] of cache.current) {
            const [kn, ka] = key.split('|');
            if (ka===a && n.startsWith(kn) && (val.complete || kn===n)) {
                return val.places.filter((p) => matchesSearch(p, n, a));
            }
        }
        const places = await fetchPlaces(name, area);
        cache.current.set(n+'|'+a, {places, complete: places.length<searchTuning.serverMaxResults});
        return places;
    };

    const special = textAction && onEnterText && trimmed ? textAction(trimmed) : null;
    useEffect(() => {
        const {namePart, areaPart} = parseSearch(trimmed);
        if (namePart.length<MIN_CHARS || special) {
            requestId.current++;
            setLoading(false);
            return;
        }
        const id = ++requestId.current;
        setLoading(true);
        // As in the Place dropdown, the part after a comma only narrows once it is 3+ characters
        const area = areaPart.length>=3 ? areaPart : '';
        const timer = setTimeout(async () => {
            const names = [namePart];
            const spaced = postcodeWithSpace(namePart);
            if (spaced) names.push(spaced);
            const lists = await Promise.all(names.map((n) => lookup(n, area)));
            if (id!==requestId.current) return;
            const merged = [];
            lists.forEach((list, i) => list.forEach((p) => {
                if (matchesSearch(p, names[i].toLowerCase(), areaPart.toLowerCase()) && !merged.some((m) => samePlace(m, p))) {
                    merged.push(p);
                }
            }));
            setResults({query:trimmed, places:merged});
            setLoading(false);
        }, DEBOUNCE_MS);
        return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trimmed]);

    const view = useMemo(() => {
        let sections;
        let message = null;
        if (!trimmed) {
            sections = [{title:null, items:[{kind:'location'}]}];
            if (recents.length>0) sections.push({title:'Recent', items:recents.map((place) => ({kind:'recent', place}))});
        }
        else if (parseSearch(trimmed).namePart.length<MIN_CHARS) {
            sections = [];
            message = 'Keep typing: a postcode, area, town, station or building';
        }
        else if (!results) {
            sections = [];
            message = 'Searching…';
        }
        else {
            // Older matches stay on show while newer ones load
            sections = buildSections(results.places, results.query);
            if (sections.length===0) message = loading ? 'Searching…' : 'No places found for "'+trimmed+'"';
        }
        // The full-screen phone search keeps current location as its first row while
        // typing too; the highlight still starts on the top match below it
        let first = 0;
        if (isMobile && trimmed) {
            sections = [{title:null, items:[{kind:'location'}]}, ...sections];
            if (sections.length>1) first = 1;
        }
        let index = 0;
        sections.forEach((section) => section.items.forEach((item) => { item.index = index++; }));
        return {sections, message, count:index, first, current:!trimmed || (results && results.query===trimmed)};
    }, [trimmed, results, recents, loading, isMobile]);

    useEffect(() => { setSel(view.first); }, [view.first, trimmed, results]);

    const itemAt = (index) => {
        for (const section of view.sections) {
            const found = section.items.find((item) => item.index===index);
            if (found) return found;
        }
        return null;
    };

    const close = () => {
        setOpen(false);
        pendingEnter.current = false;
        if (inputRef.current) inputRef.current.blur();
    };

    const choose = (item) => {
        if (!item) return;
        // Which places people look for (the admin portal's Traffic page); a full postcode
        // only by its district, so no one's exact address is kept
        if (item.kind !== 'location' && item.place && item.place.name) {
            track('search_pick', groupOf(item.place) === 'postcode' ? item.place.name.split(' ')[0] : item.place.name);
        }
        if (detached) {
            if (item.kind==='location') {
                if (navigator.geolocation) navigator.geolocation.getCurrentPosition((pos) => {
                    const [x, y] = fromLonLat([pos.coords.longitude, pos.coords.latitude]);
                    if (onChoose) onChoose({kind:'location', place:{x, y, name:'Your location'}});
                }, () => {}, {timeout:10000});
            } else {
                addRecentPlace(item.place);
                if (onChoose) onChoose(item);
            }
            setQuery('');
            close();
            return;
        }
        if (item.kind==='location') {
            getUserLocation(dispatch, true);
        }
        else {
            goToPlace(dispatch, item.place, selectedLayers);
            addRecentPlace(item.place);
            notePlaceSearched();
        }
        setQuery('');
        close();
        if (onChoose) onChoose(item);
    };

    // Enter pressed early: take the top match as soon as the matches for the typed text land
    useEffect(() => {
        if (pendingEnter.current && view.current && !loading) {
            pendingEnter.current = false;
            const top = itemAt(view.first);
            if (top && top.kind==='place') choose(top);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, loading]);

    const onKeyDown = (e) => {
        if (e.key==='ArrowDown') {
            e.preventDefault();
            setOpen(true);
            setSel((s) => Math.min(view.count-1, s+1));
        }
        else if (e.key==='ArrowUp') {
            e.preventDefault();
            setSel((s) => Math.max(0, s-1));
        }
        else if (e.key==='Enter') {
            e.preventDefault();
            if (onEnterText && trimmed && onEnterText(trimmed)) {
                setQuery('');
                close();
                return;
            }
            if (trimmed && (!view.current || loading)) {
                pendingEnter.current = true;
                return;
            }
            const item = itemAt(sel);
            // With nothing found, the only row left is current location: Enter does not take it
            if (trimmed && item && item.kind==='location' && view.count===1) {
                track('search_none', trimmed.slice(0, 60));
                return;
            }
            choose(item);
        }
        else if (e.key==='Escape') {
            e.preventDefault();
            if (query) setQuery('');
            else close();
        }
    };

    const focusSearch = () => {
        if (onLocked) { onLocked(); return; }
        setOpen(true);
        if (!isMobile && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    };

    // "/" from anywhere that is not a text field, and requests from elsewhere in the app
    useEffect(() => {
        const onKey = (e) => {
            if (e.key!=='/' || e.ctrlKey || e.metaKey || e.altKey) return;
            const el = document.activeElement;
            const tag = el && el.tagName;
            if (tag==='INPUT' || tag==='TEXTAREA' || tag==='SELECT' || (el && el.isContentEditable)) return;
            e.preventDefault();
            focusSearch();
        };
        document.addEventListener('keydown', onKey);
        window.addEventListener(FOCUS_SEARCH_EVENT, focusSearch);
        return () => {
            document.removeEventListener('keydown', onKey);
            window.removeEventListener(FOCUS_SEARCH_EVENT, focusSearch);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMobile]);

    useEffect(() => {
        const onChange = () => setRecents(readRecents());
        window.addEventListener(RECENTS_EVENT, onChange);
        return () => window.removeEventListener(RECENTS_EVENT, onChange);
    }, []);

    // On desktop, a click anywhere outside closes the list
    useEffect(() => {
        if (!open || isMobile) return;
        const onDown = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('touchstart', onDown);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('touchstart', onDown);
        };
    }, [open, isMobile]);

    // The clear button empties what was typed and keeps the search open
    const clear = () => {
        setQuery('');
        focusSearch();
    };

    const listId = isMobile ? 'tms-search-list-m' : 'tms-search-list';
    const input = (
        <input ref={inputRef} type="search" role="combobox" autoComplete="off" spellCheck={false}
            enterKeyHint="search" aria-label="Search for a place" aria-autocomplete="list"
            aria-expanded={open} aria-controls={listId}
            aria-activedescendant={open && view.count>0 ? listId+'-opt-'+sel : undefined}
            placeholder={locationName || placeholder || (isMobile ? 'Search a place' : 'Postcode, area, town, station or building')}
            value={open ? query : (query || locationName)} autoFocus={isMobile}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)} onKeyDown={onKeyDown}/>
    );
    const spinner = loading && trimmed ? <span className="spin" aria-hidden="true"/> : null;
    const runSpecial = (row) => {
        if (row.run) { row.run(); setQuery(''); close(); }
        else if (onEnterText(trimmed)) { setQuery(''); close(); }
    };
    const list = special
        ? <div className="tms-search-special" role="listbox" id={listId} aria-label="What to do with this">
            {[].concat(special).map((row, i) => (
                <button key={row.label} type="button" role="option" aria-selected={i === 0} onMouseDown={(e) => e.preventDefault()}
                    onClick={() => runSpecial(row)}>
                    <b>{row.label}</b>{row.sub && <span>{row.sub}</span>}
                </button>))}
        </div>
        : <ResultsList listId={listId} view={view} sel={sel} setSel={setSel} choose={choose} query={results ? results.query : ''}/>;

    if (onLocked) {
        const label = locationName || placeholder || (isMobile ? 'Search a place' : 'Postcode, area, town, station or building');
        return (
            <div className={'tms-search locked'+(isMobile ? ' mobile' : '')}>
                <button type="button" className={isMobile ? 'tap' : 'box'} onClick={onLocked}
                    aria-label="Search for a place, for subscribers" title="Subscribe to search for a place">
                    <SearchIcon/><span className="plh">{label}</span><LockIcon/>
                </button>
            </div>
        );
    }

    if (isMobile) {
        return (
            <>
            <div className="tms-search mobile">
                <button type="button" className="tap" aria-label={locationName ? 'Search for a place, now '+locationName : 'Search for a place'}
                    onClick={() => setOpen(true)}>
                    <SearchIcon/><span className={locationName ? 'plh name' : 'plh'}>{locationName || placeholder || 'Search a place'}</span>
                </button>
                <button type="button" className="loc" aria-label="Use my current location" title="Current location"
                    onClick={() => choose({kind:'location'})}>
                    <Icon name="location" size={18}/>
                </button>
            </div>
            {open && createPortal(
                <div className="tms-search-full" role="dialog" aria-modal="true" aria-label="Search for a place">
                    <div className="bar">
                        <button type="button" className="back" aria-label="Back to the map" onClick={close}>
                            <svg viewBox="0 0 16 16" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8"
                                strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M10 3L5 8l5 5"/></svg>
                        </button>
                        <div className="in">{input}{spinner}
                            {query &&
                                <button type="button" className="clr" aria-label="Clear the search" onClick={() => setQuery('')}>
                                    <ClearIcon/>
                                </button>}
                        </div>
                    </div>
                    {list}
                </div>,
                document.body)}
            </>
        );
    }

    return (
        <div className="tms-search" ref={rootRef}>
            <div className={'box'+(open ? ' open' : '')}>
                <SearchIcon/>
                {input}
                {spinner}
                {/* No clear button for the location's name: clicking the box empties it anyway */}
                {query ?
                    <button type="button" className="clr" title="Clear" aria-label="Clear the search"
                        onMouseDown={(e) => e.preventDefault()} onClick={clear}>
                        <ClearIcon/>
                    </button> :
                    <kbd aria-hidden="true">/</kbd>}
            </div>
            {open && <div className="drop">{list}</div>}
        </div>
    );
}
